import React, { ReactNode, useEffect } from "react";

import { Container, Stack, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router";

import { ORGANIZATIONS } from "src/platform/constants/routes";
import { eventBus } from "src/platform/events/EventBus";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { AppBreadcrumbs } from "src/shell/components/AppBreadcrumbs";
import { DotGrid } from "src/shell/components/DotGrid";
import ErrorCard from "src/shell/components/ErrorCard";
import { getContainerStyles } from "src/shell/styles/themeUtils";

import { useOrganizationLoader } from "../hooks/useOrganizationLoader";
import type { Organization } from "../types";

import OrganizationDetailsSkeleton from "./OrganizationDetails/OrganizationDetailsSkeleton";
import OrganizationHeader from "./OrganizationDetails/OrganizationHeader";

export type OrganizationPageContent = (
  organization: Organization,
  reload: () => Promise<void>,
) => ReactNode;

interface OrganizationPageShellProps {
  pageTitle?: string;
  children: ReactNode | OrganizationPageContent;
}

const OrganizationPageShell: React.FC<OrganizationPageShellProps> = ({
  pageTitle,
  children,
}) => {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { setActiveOrg } = useActiveOrganization();
  const {
    data: organization,
    loading,
    error,
    loadOrganization,
  } = useOrganizationLoader(eventBus);

  useEffect(() => {
    if (orgAlias) {
      void loadOrganization(orgAlias);
      setActiveOrg(orgAlias);
    }
  }, [orgAlias, loadOrganization, setActiveOrg]);

  const content = organization
    ? typeof children === "function"
      ? children(organization, async () => {
          await loadOrganization(organization.alias);
        })
      : children
    : null;

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            { label: orgAlias ?? "Loading…" },
            ...(pageTitle ? [{ label: pageTitle }] : []),
          ]}
        />
        <OrganizationDetailsSkeleton organizationName={orgAlias} />
      </Container>
    );
  }

  if (error) {
    return (
      <ErrorCard
        error={error}
        title="Unable to load organization"
        onRetry={orgAlias ? () => void loadOrganization(orgAlias) : undefined}
      />
    );
  }

  if (!organization) {
    return (
      <ErrorCard
        error="No organization selected. Choose an organization from the list."
        title="No organization selected"
      />
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      <AppBreadcrumbs
        items={[
          { label: "Organizations", to: ORGANIZATIONS.LIST.path },
          {
            label: organization.name,
            to: ORGANIZATIONS.DETAILS.getPath(organization.alias),
          },
          ...(pageTitle ? [{ label: pageTitle }] : []),
        ]}
      />
      <DotGrid>
        <Stack spacing={4}>
          <OrganizationHeader
            organization={organization}
            onEditOrganization={() =>
              void navigate(
                ORGANIZATIONS.ORG_SETTINGS.getPath(organization.alias),
              )
            }
          />
          {content}
        </Stack>
      </DotGrid>
    </Container>
  );
};

export default OrganizationPageShell;
